import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { PlaylistAddCheck as LinkIcon } from "@mui/icons-material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AppDialog from "components/common/AppDialog";
import {
  SsnUpdaterBlocked,
  SsnUpdaterEntry,
  SsnUpdaterEstimate,
  SsnUpdaterRun,
  useGetJobSsnUpdaterQuery,
  useRunJobMatchesPreflightMutation,
  useStartJobSsnUpdaterMutation,
} from "features/adminUsers/adminUsers";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { formatApiError } from "utils/formatApiError";

export interface SsnUpdaterDialogProps {
  /** The upload job the dialog is about; null keeps it closed. */
  jobId: number | null;
  jobName: string;
  canEdit: boolean;
  /** Why the operator may only look (Client View, manager role); null when they may act. */
  readOnlyReason: string | null;
  onClose: () => void;
}

const POLL_MS = 2000;
const LABEL_SX = {
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  fontWeight: 700,
  color: "text.secondary",
};

const OUTCOME_LABELS: Record<string, string> = {
  exact: "linked - same name",
  similar: "linked - similar name",
  already_present: "already in the SSN file",
  full_ssn: "whole SSN in the name - resolves by itself",
  ambiguous: "several employees fit",
  no_last4: "no SSN ends with these digits",
  no_name_match: "the digits fit, the name does not",
  not_ssn_tail: "the tail is not an SSN",
  no_separator: 'no " - " in the name',
};

type EntryFilter = "all" | "linked" | "unlinked";

function errorPayload(error: unknown): unknown {
  return (error as { data?: unknown })?.data;
}

function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function isLinked(entry: SsnUpdaterEntry): boolean {
  return entry.outcome === "exact" || entry.outcome === "similar";
}

function CountRow({
  label,
  value,
  hint,
}: {
  label: string;
  value: number | string;
  hint?: string;
}) {
  return (
    <TableRow>
      <TableCell sx={{ py: 0.5 }}>
        {hint ? (
          <Tooltip title={hint}>
            <span style={{ cursor: "help" }}>{label}</span>
          </Tooltip>
        ) : (
          label
        )}
      </TableCell>
      <TableCell align="right" sx={{ py: 0.5, fontWeight: 600 }}>
        {value}
      </TableCell>
    </TableRow>
  );
}

/** What the dialog shows, from an upload job's pre-run check or a split job's
 * SSN check - the same counts either way. */
export interface SsnUpdaterCheck {
  status?: string;
  checked_at?: string | null;
  updater_estimate?: SsnUpdaterEstimate | null;
  ssn_updater?: { status?: string; state?: string | null } | null;
}

export interface SsnUpdaterReadiness {
  algorithm: string;
  reason: string;
  source_vendor?: string | null;
  destination_vendor?: string | null;
}

export interface SsnUpdaterDialogViewProps {
  open: boolean;
  /** A change starts the dialog afresh (another job). */
  resetKey: string | number | null;
  /** upload: an upload job's folders; split: a saved split result. */
  variant: "upload" | "split";
  jobName: string;
  canEdit: boolean;
  readOnlyReason: string | null;
  onClose: () => void;
  hasData: boolean;
  loading: boolean;
  loadError: unknown;
  check: SsnUpdaterCheck | null;
  readiness: SsnUpdaterReadiness | null;
  /** The file is also upload jobs' destination file: SSN Updater stays off it. */
  blocked: SsnUpdaterBlocked | null;
  run: SsnUpdaterRun | null;
  onRecheck: () => void;
  /** Starts a run; resolves with its run id, rejects with the API error. */
  onStart: (allowSimilar: boolean) => Promise<string>;
  starting: boolean;
  refetch: () => void;
}

/**
 * SSN Updater for employee folders that carry the last four digits of the SSN
 * (the PDF Splitter pattern): the estimate from the check, the run, its
 * outcome per folder. The run modifies the SOURCE vendor's SSN file, so the
 * dialog says so before the operator starts it.
 */
export function SsnUpdaterDialogView({
  open,
  resetKey,
  variant,
  jobName,
  canEdit,
  readOnlyReason,
  onClose,
  hasData,
  loading: loadingData,
  loadError,
  check,
  readiness,
  blocked,
  run,
  onRecheck,
  onStart,
  starting,
  refetch,
}: SsnUpdaterDialogViewProps) {
  const dispatch = useAppDispatch();
  const split = variant === "split";
  const running =
    run?.state === "running" ||
    (check?.ssn_updater?.state ?? check?.ssn_updater?.status) === "running";
  const checking = check?.status === "running";

  const [allowSimilar, setAllowSimilar] = useState(true);
  const [startError, setStartError] = useState<string | null>(null);
  const [entryFilter, setEntryFilter] = useState<EntryFilter>("unlinked");
  const [startedRunId, setStartedRunId] = useState<string | null>(null);
  const recheckedForRef = useRef<string | number | null>(null);
  const wasRunningRef = useRef(false);

  // A fresh dialog per job.
  useEffect(() => {
    setAllowSimilar(true);
    setStartError(null);
    setEntryFilter("unlinked");
    setStartedRunId(null);
    recheckedForRef.current = null;
    wasRunningRef.current = false;
  }, [resetKey]);

  // The estimate comes from the check; a job never checked (or whose check
  // failed) is checked once when the dialog opens.
  useEffect(() => {
    if (!open || !hasData || resetKey == null || recheckedForRef.current === resetKey)
      return;
    if (check == null || check.status === "error") {
      recheckedForRef.current = resetKey;
      onRecheck();
    }
  }, [open, hasData, resetKey, check, onRecheck]);

  useEffect(() => {
    if (!open || !(running || checking)) return undefined;
    const timer = window.setInterval(refetch, POLL_MS);
    return () => window.clearInterval(timer);
  }, [open, running, checking, refetch]);

  // One toast when the run the dialog watched comes to an end.
  useEffect(() => {
    if (running) {
      wasRunningRef.current = true;
      return;
    }
    if (!wasRunningRef.current || !run || run.state === "running") return;
    wasRunningRef.current = false;
    dispatch(
      pushNotification({
        severity: run.state === "done" ? "success" : "warning",
        message:
          run.state === "done"
            ? `SSN Updater filed ${run.counts.added ?? 0} folder name(s) under their SSNs.`
            : `SSN Updater ${run.state}: ${run.error ?? "unknown error"}.`,
      }),
    );
  }, [running, run, dispatch]);

  const estimate =
    check?.status === "done" ? (check.updater_estimate ?? null) : null;
  const linkable =
    (estimate?.linkable_exact ?? 0) +
    (allowSimilar ? (estimate?.linkable_similar ?? 0) : 0);
  const loading = open && !hasData && loadingData;
  // Reads "the source SSN file" until the readiness names the vendor.
  const vendor = readiness?.source_vendor ?? "source";

  const startDisabledReason = readOnlyReason
    ? readOnlyReason
    : !readiness
      ? ""
      : readiness.algorithm !== "ssn"
        ? `SSN matching is not available: ${readiness.reason}.`
        : blocked
          ? blocked.message
          : checking
          ? "The matches are being checked - wait for the count."
          : !estimate
            ? "The matches have not been checked yet."
            : linkable === 0
              ? "No folder can be linked by its last four digits and name."
              : running
                ? "A run is in progress."
                : "";
  const canStart =
    canEdit && !starting && startDisabledReason === "" && !!estimate;

  const handleStart = async () => {
    setStartError(null);
    try {
      setStartedRunId(await onStart(allowSimilar));
      setEntryFilter("unlinked");
      refetch();
    } catch (error) {
      const payload = errorPayload(error) as { message?: string } | undefined;
      setStartError(
        payload?.message ??
          formatApiError(payload, "The SSN Updater could not be started."),
      );
    }
  };

  const entries = useMemo(() => run?.entries ?? [], [run]);
  const shownEntries = useMemo(() => {
    if (entryFilter === "all") return entries;
    return entries.filter((entry) =>
      entryFilter === "linked" ? isLinked(entry) : !isLinked(entry),
    );
  }, [entries, entryFilter]);
  const showRun =
    run != null &&
    (run.state !== "running" ||
      startedRunId == null ||
      run.run_id === startedRunId);

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseIcon
      title={
        <Stack spacing={0.25}>
          <Typography
            variant="h6"
            component="span"
            sx={{ fontWeight: 800, lineHeight: 1.2 }}
          >
            SSN Updater
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            {jobName}
          </Typography>
        </Stack>
      }
      actions={
        <>
          <Button onClick={onClose}>Close</Button>
          <Tooltip title={canStart ? "" : startDisabledReason}>
            <span>
              <Button
                variant="contained"
                startIcon={
                  starting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <LinkIcon />
                  )
                }
                disabled={!canStart}
                onClick={() => void handleStart()}
              >
                {starting
                  ? "Starting..."
                  : run && run.state !== "running"
                    ? `Run again (${linkable} to link)`
                    : `Link ${linkable} folder${linkable === 1 ? "" : "s"}`}
              </Button>
            </span>
          </Tooltip>
        </>
      }
    >
      <Stack spacing={2} sx={{ pb: 1 }}>
        {loading && (
          <Stack spacing={1}>
            <Skeleton variant="rounded" height={56} />
            <Skeleton variant="rounded" height={160} />
          </Stack>
        )}
        {loadError != null && !hasData && (
          <Alert severity="error">
            {formatApiError(
              errorPayload(loadError),
              "The SSN Updater status could not be read.",
            )}
          </Alert>
        )}

        {hasData && (
          <Alert severity="info">
            The folders of this {split ? "split result" : "job"} are named{" "}
            <b>{"<name> - <last 4 digits of the SSN>"}</b> (the way the PDF
            Splitter names them), while the {vendor} SSN file knows employees by
            their {readiness?.source_vendor ?? "vendor"} ids. SSN Updater finds
            the SSN whose last four digits and name fit each folder and files
            the folder name under it in the <b>{vendor} SSN file</b> - the key
            the upload bot joins on.{" "}
            {split
              ? "The SSN Job of this major job collects that file, and every upload job of this client reading these documents uses it;"
              : `That file is shared by every upload job of this client reading ${readiness?.source_vendor ?? "that vendor's"} documents;`}{" "}
            earlier versions stay in the storage&apos;s version history.
          </Alert>
        )}

        {blocked && (
          <Alert severity="warning">
            SSN Updater does not write here: {blocked.message}
          </Alert>
        )}

        {readiness && readiness.algorithm !== "ssn" && (
          <Alert severity="warning">
            SSN matching is not available: {readiness.reason}.
          </Alert>
        )}

        {hasData && checking && (
          <Alert severity="info" icon={<CircularProgress size={18} />}>
            Checking the employee folders against the SSN file...
          </Alert>
        )}

        {estimate && readiness?.algorithm === "ssn" && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="caption" sx={LABEL_SX}>
              What the run would do
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                <CountRow
                  label="Employee folders in Data Storage"
                  value={estimate.folders}
                />
                <CountRow
                  label="Named with the last 4 digits of the SSN"
                  value={estimate.four_digit_tails}
                />
                <CountRow
                  label={`Linkable by exact name`}
                  value={estimate.linkable_exact}
                  hint="The last four digits and the name agree with one employee in the SSN file."
                />
                <CountRow
                  label="Linkable by similar name"
                  value={estimate.linkable_similar}
                  hint="The digits agree and the names differ in a middle name, an initial or a spelling; first and last name have to agree."
                />
                {estimate.would_match != null && (
                  <CountRow
                    label={`Would then match an employee in ${readiness?.destination_vendor ?? "the destination"}`}
                    value={estimate.would_match}
                    hint="A linked folder still matches nobody when its SSN is not in the destination SSN file."
                  />
                )}
                <CountRow
                  label="Already in the SSN file"
                  value={estimate.already_linked}
                />
                <CountRow
                  label="Several employees fit"
                  value={estimate.ambiguous}
                  hint={
                    split
                      ? "Left alone - the upload job resolves these by hand through Merge reports."
                      : "Left alone - resolve these by hand through Merge reports."
                  }
                />
                <CountRow
                  label="No SSN ends with the digits"
                  value={estimate.no_last4}
                />
                <CountRow
                  label="The digits fit, the name does not"
                  value={estimate.no_name_match}
                />
                <CountRow
                  label="Other tails (vendor ids, whole SSNs, no separator)"
                  value={
                    estimate.not_ssn_tails +
                    estimate.full_ssn_tails +
                    estimate.no_separator
                  }
                />
              </TableBody>
            </Table>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  size="small"
                  checked={allowSimilar}
                  disabled={!canEdit || running}
                  onChange={(event) => setAllowSimilar(event.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Also link similar names ({estimate.linkable_similar}) - off
                  links exact names only
                </Typography>
              }
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Checked {formatWhen(check?.checked_at)}.{" "}
              {split
                ? `After the run the upload jobs of this client reading ${vendor} documents are checked again; their Matches file is built on the next Build matches only or Run all.`
                : "After the run the Matches file is built on the next Build matches only or Run all - an empty stored Matches file is rebuilt by itself."}
            </Typography>
          </Paper>
        )}

        {startError && <Alert severity="error">{startError}</Alert>}

        {showRun && run && (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 0.5 }}
            >
              <Typography variant="caption" sx={LABEL_SX}>
                {run.state === "running" ? "Run in progress" : "Last run"}
              </Typography>
              <Chip
                size="small"
                label={run.state}
                color={
                  run.state === "done"
                    ? "success"
                    : run.state === "running"
                      ? "info"
                      : "warning"
                }
                variant="outlined"
              />
              <Typography variant="caption" color="text.secondary">
                {formatWhen(run.finished_at ?? run.started_at)}
                {run.started_by?.username
                  ? ` by ${run.started_by.username}`
                  : ""}
                {run.allow_similar ? "" : " - exact names only"}
              </Typography>
            </Stack>
            {run.state === "running" && <LinearProgress sx={{ mb: 1 }} />}
            {run.state === "interrupted" && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                The run stopped reporting - nothing more will happen. Run it
                again.
              </Alert>
            )}
            {run.state === "failed" && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {run.error ?? "The run failed."}
              </Alert>
            )}
            {run.state === "done" && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                Filed <b>{run.counts.added ?? 0}</b> folder name(s) under their
                SSNs
                {run.counts.would_match != null
                  ? `; ${run.counts.would_match} of them have an employee in ${run.destination_vendor}`
                  : ""}
                .{" "}
                {split
                  ? "The folders and the upload jobs reading them were checked again."
                  : "The job's matches were re-checked."}
              </Typography>
            )}
            {entries.length > 0 && (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 0.5 }}
                >
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={entryFilter}
                    onChange={(_, value: EntryFilter | null) =>
                      value && setEntryFilter(value)
                    }
                  >
                    <ToggleButton value="unlinked">
                      Unlinked (
                      {entries.filter((entry) => !isLinked(entry)).length})
                    </ToggleButton>
                    <ToggleButton value="linked">
                      Linked ({entries.filter(isLinked).length})
                    </ToggleButton>
                    <ToggleButton value="all">
                      All ({entries.length})
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Box sx={{ maxHeight: 320, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Folder</TableCell>
                        <TableCell>Outcome</TableCell>
                        <TableCell align="right">Name score</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {shownEntries.map((entry) => (
                        <TableRow key={entry.folder}>
                          <TableCell
                            sx={{ fontFamily: "monospace", fontSize: 12 }}
                          >
                            {entry.folder}
                          </TableCell>
                          <TableCell>
                            {OUTCOME_LABELS[entry.outcome] ?? entry.outcome}
                          </TableCell>
                          <TableCell align="right">
                            {entry.score != null ? Math.round(entry.score) : ""}
                          </TableCell>
                        </TableRow>
                      ))}
                      {shownEntries.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={3}>
                            <Typography variant="body2" color="text.secondary">
                              Nothing here.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Paper>
        )}
      </Stack>
    </AppDialog>
  );
}

/**
 * SSN Updater of an upload job: the estimate comes from its pre-run matches
 * check (TaskJob.matches_preflight).
 */
export default function SsnUpdaterDialog({
  jobId,
  jobName,
  canEdit,
  readOnlyReason,
  onClose,
}: SsnUpdaterDialogProps) {
  const open = jobId != null;
  const status = useGetJobSsnUpdaterQuery(
    { jobId: jobId ?? 0, includeEntries: true },
    { skip: !open, refetchOnMountOrArgChange: true },
  );
  const data = status.currentData;
  const preflight = data?.matches_preflight ?? null;
  const [recheck] = useRunJobMatchesPreflightMutation();
  const [startUpdater, { isLoading: starting }] = useStartJobSsnUpdaterMutation();
  const { refetch } = status;
  // A read-only viewer never starts a check, not even the one on open.
  const handleRecheck = useCallback(() => {
    if (jobId != null && canEdit) void recheck(jobId);
  }, [jobId, canEdit, recheck]);
  const handleStart = useCallback(
    async (allowSimilar: boolean) => {
      const response = await startUpdater({ jobId: jobId ?? 0, allowSimilar }).unwrap();
      return response.run.run_id;
    },
    [jobId, startUpdater],
  );
  const handleRefetch = useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <SsnUpdaterDialogView
      open={open}
      resetKey={jobId}
      variant="upload"
      jobName={jobName}
      canEdit={canEdit}
      readOnlyReason={readOnlyReason}
      onClose={onClose}
      hasData={!!data}
      loading={status.isLoading || status.isFetching}
      loadError={status.error ?? null}
      check={preflight}
      readiness={data?.readiness ?? null}
      blocked={preflight?.updater_blocked ?? null}
      run={data?.run ?? null}
      onRecheck={handleRecheck}
      onStart={handleStart}
      starting={starting}
      refetch={handleRefetch}
    />
  );
}
